/**
 * RWBR — a whole race, green flag to checkered (rulebook p.ii, p.xi, p.xv).
 *
 * "Each race consists of 20 to 60 turns… Once the final turn is complete,
 *  whichever driver occupies the race LEADER space is considered to have won the
 *  race. The final race order can then be determined by the position of the
 *  driver cards on the board and the BOTTOM stack."                     — p.ii
 *
 * The closing rules this module owns, all from p.xi and p.xv:
 *   • PIT TURNS are pre-determined by race length — 7, then every tenth turn.
 *   • A race CANNOT END on a yellow flag. "If it would, conduct an additional
 *     turn." That covers both a turn run under caution and a turn that brings
 *     the caution out.
 *   • On the FINAL TURN only, BOTTOM-group results are disregarded and a black 5
 *     checks the called quality in MIDDLE and then TOP. (turn.ts applies it; this
 *     module decides which turn is final.)
 *   • LAPS LED and the BOTTOM-group chip ban are enforced every turn by turn.ts.
 *
 * PURITY. Nothing outside the arguments is read or written; `runRace` builds its
 * own `Dice` from the seed (D-005) and returns every state it passed through.
 * That still holds under D-018 manual dice: the hand-rolled faces arrive as an
 * ARGUMENT (`RaceOptions.dice`) and the `Dice` object is still built here, so
 * `runRace` remains a pure function of (setup, options) — which is the property
 * the whole pause mechanism is built on. See `manualRace.ts`.
 */

import type { Car, DriverCard, FeudEntry, Quality, RaceGrants, RaceState } from './types.ts';
import { retainTurnDraws, retainFinishDraws } from './diceTrace.ts';
import { Dice, type DiceFeed, type DieDraw, type ObservedDraw } from './dice.ts';
import { grantsAreEmpty } from './interviewEffects.ts';
import { resolvePhotoFinish, type PhotoFinishResult } from './photoFinish.ts';
import { ChurnTracker } from './bottomGroupChurn.ts';
import { BGS_TRIGGER_FACES_DEFAULT, SurgeTracker } from './bottomGroupSurge.ts';
import { isRestartTurn } from './restartEvent.ts';
import type { RuleEvent } from './ruleEvent.ts';
import { renumber, stampDiceEntry } from './ruleEvent.ts';
import { pitTurnsFor } from './pitTurn.ts';
import { ChartBTracker, trackTypeOf } from './troubleMatrix.ts';
import { LapsDownTracker } from './lapsDown.ts';
import { runTurn, trackCard, type RaceCalled, type TrackCard, type TurnOptions, type TurnResult } from './turn.ts';
import type { CareerConsequence } from './careerConsequences.ts';
import { houseRulesInForce, type HouseRuleOptions } from './houseRules.ts';

export const CITE_ENDING = 'RB/xv — ENDING THE RACE';
export const CITE_NO_YELLOW_FINISH =
  'RB/xi & RB/xv — "A race cannot end on a YELLOW FLAG turn. If it would, conduct an additional turn."';

// ---------------------------------------------------------------------------
// Building the grid
// ---------------------------------------------------------------------------

export interface RaceSetup {
  /** A track card, or the id of one in src/data/track-cards.json. */
  track: TrackCard | string;
  /** The starting field, in starting order — the RACE WEEK result (p.i). */
  drivers: DriverCard[];
  seed: number;
  /** Defaults to the track card's suggested length. */
  totalTurns?: number;
  raceId?: string;
  raceName?: string;
  /** RACE WEEK's temporary TV quality, by driver id. */
  tvList?: Record<string, Quality>;
  feuds?: FeudEntry[];
  fuming?: string[];
  /**
   * Performance chips a driver already holds when the flag drops — RACE WEEK's
   * own `chips` (RWEG/p.3, "PLACEMENT A, plus Performance Chip").
   *
   * Without this the chip cannot survive the trip from qualifying to the green:
   * `runRace` rebuilds the grid from this setup, so anything applied to a state
   * built earlier is thrown away. The BOTTOM-group ban (p.xv) is still enforced
   * — `renumber` strips a chip off any car that lines up 20th or worse, which
   * is belt and braces over the forfeit race week already applied.
   */
  startingChips?: Record<string, number>;
  /**
   * One-race POST RACE INTERVIEW grants, by driver id (D-EZ-15).
   *
   * The card edits an interview produces arrive already baked into `drivers` —
   * a bigger ESCAPE is just a different card. These are the effects a card
   * cannot express because they are about *when*: the first third, the first
   * escape roll, one time only. They land on `Car.grants` and are struck off as
   * they are spent.
   */
  grants?: Record<string, RaceGrants>;
}

export function resolveTrack(track: TrackCard | string): TrackCard {
  return typeof track === 'string' ? trackCard(track) : track;
}

/**
 * p.i — "Your cars are now lined up, ready to go, based on results of Race
 * Week." The first card is the pole sitter and becomes the race LEADER.
 */
export function buildStartingGrid(setup: RaceSetup): RaceState {
  const track = resolveTrack(setup.track);
  const totalTurns = setup.totalTurns ?? track.turns;
  if (!Number.isInteger(totalTurns) || totalTurns < 1) {
    throw new RangeError(`a race must be a positive whole number of turns, got ${totalTurns}`);
  }
  const seen = new Set<string>();
  const grid: Car[] = setup.drivers.map((card, i) => {
    if (seen.has(card.driverId)) throw new Error(`duplicate driver in the field: ${card.driverId}`);
    seen.add(card.driverId);
    const carried = setup.startingChips?.[card.driverId] ?? 0;
    const granted = setup.grants?.[card.driverId];
    return {
      card,
      position: i + 1,
      chips: carried > 0 ? Math.floor(carried) : 0,
      tvQuality: setup.tvList?.[card.driverId] ?? null,
      status: 'running',
      lapsDown: false,
      consecutiveTurnsLed: 0,
      turnsLed: 0,
      ...(granted && !grantsAreEmpty(granted) ? { grants: { ...granted } } : {}),
    };
  });

  return {
    raceId: setup.raceId ?? `${track.id}-${setup.seed}`,
    trackId: track.id,
    raceName: setup.raceName ?? track.raceName ?? track.name,
    totalTurns,
    turn: 1,
    grid: renumber(grid).order,
    retired: [],
    pitTurns: pitTurnsFor(totalTurns),
    yellowTurnsRemaining: 0,
    feuds: (setup.feuds ?? []).map((f) => ({ ...f })),
    fuming: [...(setup.fuming ?? [])],
    finished: false,
    lowFuel: [],
  };
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export interface FinishEntry {
  /** 1-based finishing position. */
  place: number;
  driverId: string;
  name: string;
  status: 'running' | 'out';
  outReason?: Car['outReason'];
  /** The turn he left, for cars that did not finish. */
  outTurn?: number;
  turnsLed: number;
  chips: number;
}

export interface LeadChange {
  turn: number;
  driverId: string;
}

export interface Caution {
  /** The turn the yellow came out. */
  turn: number;
  /** How many caution turns it cost (p.v). */
  turns: number;
  cars: string[];
}

export interface RaceResult {
  /** Actual presentation throws; optional for older saved races. */
  finishDiceReceipt?: import('./diceTrace.ts').SavedDiceReceipt;
  /** Actual grid before turn one; absent in older saved results. */
  startingOrder?: string[];
  state: RaceState;
  track: TrackCard;
  /** Every turn, in order, with its own events and the state it produced. */
  turns: TurnResult[];
  /** Every event of the race, flattened, with the turn it happened on. */
  events: (RuleEvent & { turn: number })[];
  finishingOrder: FinishEntry[];
  winnerId: string | null;
  leadChanges: LeadChange[];
  cautions: Caution[];
  /**
   * Turns actually run — never fewer than the scheduled length (p.xv), EXCEPT
   * when the race was `called` (D-EZ-88: rain past halfway, or the event
   * cancelled), which the RARE RESULTS sheet prints as ending it where it stands.
   */
  turnsRun: number;
  scheduledTurns: number;
  retirements: FinishEntry[];
  /**
   * What the laps-down rule did over the race, or null when it was off.
   *
   * `clamped` is the number of cards the boundary clamp had to put back — a
   * swap that tried to move a car across the laps-down marker. It is surfaced
   * rather than swallowed: a correction nobody counts is indistinguishable from
   * a bug nobody notices, and this one was found by counting.
   */
  lapsDown: { clamped: number } | null;
  /**
   * The EZRA photo finish, or null when the rule was off. `attempts` is empty
   * when it ran and nobody qualified — the two are different facts.
   */
  photoFinish: PhotoFinishResult | null;
  /**
   * What the BOTTOM GROUP CHURN did over the race, or null when it was off.
   *
   * All four counts are carried, and the identity `triggered = ignored +
   * aborted + occurred` is what makes the once-per-third limit checkable from
   * outside. A limit nobody counts is indistinguishable from a limit that is
   * not being applied — the same reason `lapsDown.clamped` is here.
   */
  churn: { triggered: number; ignored: number; aborted: number; occurred: number } | null;
  /**
   * What the BOTTOM GROUP SURGE did over the race, or null when it was off — and
   * **it is off unless explicitly asked for**, because §3.13's trigger face is
   * not printed anywhere (⚠ OPEN RULING D-EZ-35).
   *
   * `triggerFaces` is carried alongside the counts and is not decoration: it is
   * the guess the race was run under. A stored race record whose Surge counts
   * cannot be attributed to a rate would be unauditable the day Howard rules.
   */
  surge: {
    triggerFaces: readonly number[];
    triggered: number;
    aborted: number;
    occurred: number;
    llsgSurges: number;
    ldsgEscapes: number;
    chipOnly: number;
    chipsAwarded: number;
    chipsRefused: number;
    finderRolls: number;
    finderRejected: number;
    ungradedEscapes: number;
  } | null;
  /**
   * D-018 — every face this race was run on, in the order it was drawn, each
   * one marked `hand` or `engine`.
   *
   * NULL FOR A SEEDED RACE, and that is the point rather than an omission. In a
   * seeded race the SEED is the stream: D-005 already guarantees the faces are
   * reproducible from one stored number, so eight hundred logged objects per
   * race would record something the record already contains — and a season is
   * thirty-six races, stored in a database.
   *
   * In a hand-rolled race the faces came off a table and exist nowhere else on
   * earth once the dice are picked up. They are kept, forever, in the saved
   * record, because that is the only thing that makes "was this 6 Howard's or
   * the machine's?" answerable after the fact.
   */
  diceStream: readonly DieDraw[] | null;
  /**
   * D-EZ-88 — the race was CALLED before its scheduled finish: rain past
   * halfway, or an evacuation that cancelled the rest of the event. Null for a
   * race that ran to the checkered flag. Optional because older saves lack it.
   * Presentation reads this to say "rain-shortened"; the finishing order is
   * already the order as it stood.
   */
  called?: RaceCalled | null;
  /**
   * D-EZ-88 — SUPER RARE consequences that outlive the race, in the order they
   * happened, dice already rolled. `seasonPlay.ts` applies them (injury and
   * suspension clocks, a fatality). Optional because older saves lack it.
   */
  careerConsequences?: CareerConsequence[];
}

export interface RaceOptions
  extends Omit<
      TurnOptions,
      | 'finalTurn'
      | 'troubleMatrix'
      | 'crewInjured'
      | 'lapsDown'
      | 'churn'
      | 'surge'
      | 'houseRules'
      | 'cleanAirTurn'
      | 'greenRunBefore'
    >,
    /**
     * The REALISM HOUSE RULES, D-EZ-83 … D-EZ-87 — track personality, pack
     * racing, laps lost to pace, clean air on restarts, the era dial. Every one
     * defaults ON ('modern'); `HOUSE_RULES_OFF` from `houseRules.ts` is the
     * printed game exactly. See that file.
     */
    HouseRuleOptions {
  /**
   * EZRA-S2 §3.13 — the BOTTOM GROUP SURGE prelude. **Default OFF**, and that is
   * the opposite of every other EZRA toggle in this interface for one reason:
   *
   *   ⚠ OPEN RULING D-EZ-35 — §3.13 prints NO trigger face. *"An BGS is activated
   *   by a D10 roll on the EZRA PRELUDE TRIGGER TABLE (PTT). (See Rules
   *   Appendix.)"* The PTT is in no supplied document and the Appendix index lists
   *   only A, B1, B2 and C. §3.12 one section earlier prints *"a D10 roll of 1"*;
   *   §3.13 prints a die and a table and nothing else.
   *
   * The PROCEDURE is fully printed and fully built. Only the RATE is unknown, and
   * the rate is the rule — so nothing fires until somebody says how often, and
   * with the option absent no die is rolled at all, which keeps every existing
   * seeded race byte-identical.
   *
   *   `surge: true`               → the documented default reading: one face, a
   *                                 d10 of 1, i.e. 10% of Pit and Final turns.
   *                                 Face 1 because it is the only face the corpus
   *                                 ever attaches to a prelude (§3.12, §3.6). It
   *                                 is a GUESS.
   *   `surge: { faces: [1, 2] }`  → the range reading, 20%. The §3.6 Restart
   *                                 table is itself a d10 with ranges on it, so
   *                                 this is a live candidate and not a strawman.
   *
   * `npm run surge` measures whatever is configured against all three candidate
   * closed forms. See `bottomGroupSurge.ts` for what would close the ruling.
   */
  surge?: boolean | { faces?: readonly number[] };
  /**
   * EZRA-S2 §3.12 — the BOTTOM GROUP CHURN prelude (D-EZ-29). Default ON; pass
   * `false` for the base game, which has no prelude phase at all.
   *
   * It adds a d10 before every eligible turn, so switching it changes every
   * downstream roll in the race — the same property the Race Start Event has,
   * and the reason a component test that wants a fixed seed pins both off.
   */
  churn?: false;
  /**
   * The laps-down sub-group (LDSG) and the Lucky Dog. Default ON; pass `false`
   * for the base game, where the BOTTOM group is one stack and a repaired car
   * simply goes to the back of it.
   *
   * This one changes the SHAPE of the field rather than a single result, so it
   * is the toggle most worth being able to turn off: with it on, a repaired car
   * is off the lead lap and can only come back by winning a challenge or by
   * catching a caution at the right moment.
   */
  lapsDown?: false;
  /**
   * D-015 — the EZRA Trouble Matrix. Default ON; pass `false` for the base
   * game's TROUBLE, which is what every published PLAAY rulebook describes.
   */
  troubleMatrix?: false;
  /**
   * D-EZ-7 — EZRA Appendix B1 pit violations. Default ON; pass `false` for the
   * base game's flat "end of the BOTTOM group" (RB/xi-xii).
   *
   * The trigger is identical either way, so this changes what a violation
   * COSTS, not how often one happens.
   */
  pitViolations?: false;
  /**
   * EZRA-S2 §3.9 — Appendix B2 pit trouble on a Fast Pit roll of 12. Default ON.
   *
   * Unlike the violations toggle, this one adds dice, so it shifts every
   * downstream roll in the race.
   */
  pitTrouble?: false;
  /**
   * The EZRA PHOTO FINISH ATTEMPT (EZRA-TP). Default ON; pass `false` for the
   * base game, where the flag falls on the order the last turn left behind.
   *
   * It is the only rule in the game that can change a WINNER after the last
   * action of the last turn — "The leader, if unchipped, can be challenged in a
   * PFA. So it is possible to win a race by a Photo Finish."
   */
  photoFinish?: false;
  /**
   * The EZRA RACE START EVENT prelude (D-EZ-22). Default ON; pass `false` for
   * the base game, which has no prelude phase at all.
   */
  restartEvents?: false;
  /**
   * Runaway guard for the "cannot end under yellow" rule. Every extra turn can
   * itself bring out a caution; 100 is far past anything a real race produces.
   */
  maxExtraTurns?: number;
  /**
   * D-018 — HAND-ROLLED FACES, in the order they came off the table.
   *
   * Absent is the default and is exactly today's behaviour: `runRace` builds a
   * seeded `Dice` and rolls its own. When present, the same `Dice` is built from
   * the same seed but draws from this queue first, and when the queue runs out
   * it STOPS — it throws `DiceNeeded` from wherever in the call stack the die
   * was wanted, which is what makes the pause mid-turn real rather than a turn's
   * worth of dice collected up front. `manualRace.ts` is what catches it; almost
   * nothing else should call `runRace` with this directly.
   *
   * The seed still matters even in hand mode: it is what the `prng` exhaustion
   * policy falls back to when the player says "that's enough, run the rest".
   */
  dice?: DiceFeed;
  /**
   * D-018 — called with each turn the moment it completes.
   *
   * It exists because of the pause. When the engine suspends for a die, the
   * throw unwinds `runRace`'s whole stack and its local `turns` array goes with
   * it, so the screen would have nothing to show for the turns that DID finish.
   * This is how they get out. In a normal race it is undefined and costs a
   * single `?.()` per turn.
   *
   * It is a REPORT, not a hook: the turn it is handed is already final, and
   * nothing it does is read back. Throwing from it will abort the race.
   */
  onTurn?: (turn: TurnResult) => void;
}

/**
 * p.ii — "The final race order can then be determined by the position of the
 * driver cards on the board and the BOTTOM stack."
 *
 * AMBIGUITY: nothing printed says where a car that was wrecked out belongs in
 * the finishing order. Scored the way a real race sheet does it — behind every
 * car still running, and among themselves in reverse order of retirement, so a
 * car that survived to turn 40 is classified ahead of one that went out on turn
 * 3. Ties within a turn are broken by where the car was when it left.
 */
export function finishingOrder(state: RaceState): FinishEntry[] {
  const running = [...state.grid].sort((a, b) => a.position - b.position);
  const retired = [...state.retired].sort((a, b) => {
    const t = (b.outTurn ?? 0) - (a.outTurn ?? 0);
    return t !== 0 ? t : a.position - b.position;
  });
  return [...running, ...retired].map((car, i) => ({
    place: i + 1,
    driverId: car.card.driverId,
    name: car.card.name,
    status: car.status,
    outReason: car.outReason,
    outTurn: car.outTurn,
    turnsLed: car.turnsLed,
    chips: car.chips,
  }));
}

// ---------------------------------------------------------------------------
// The race
// ---------------------------------------------------------------------------

export function runRace(setup: RaceSetup, options: RaceOptions = {}): RaceResult {
  const track = resolveTrack(setup.track);
  // D-018 — the feed is an argument, so this is still a `Dice` built here from
  // the seed and `runRace` is still a pure function of what it was handed. With
  // no feed the second argument is `undefined` and every line of the manual-dice
  // path inside `Dice` is dead, which is how "same seed, byte-identical race"
  // survives this change untouched.
  let presentationDraws: ObservedDraw[] = [];
  const dice = new Dice(setup.seed, options.dice, draw => presentationDraws.push(draw));
  // D-015 — ONE Chart B index card for the whole race. See the note on
  // TurnOptions.troubleMatrix: a per-turn tracker never runs out of spaces, so
  // it would ration nothing while still costing dice.
  const troubleMatrix =
    options.troubleMatrix === false
      ? null
      : new ChartBTracker(trackTypeOf(track.category), setup.totalTurns ?? track.turns);

  // EZRA-S2 Appendix B2 note * — "All remaining Fast Pit checks in this race
  // fail." One array for the whole race, for the same reason as the Chart B
  // tracker above.
  const crewInjured: string[] = [];

  // The LDSG's memory: banked challenge wins for grade D, the turn each car went
  // down, and which caution has already spent its Lucky Dog. One per race, for
  // the same reason as the Chart B tracker above — every rule it answers is a
  // rule about what happened on an EARLIER turn.
  const lapsDown = options.lapsDown === false ? null : new LapsDownTracker();

  // EZRA-S2 §3.12 — "A BGC can occur only once each one third race turn
  // increment." One tracker for the whole race, for the same reason as the
  // Chart B index card above: the entire content of the rule is memory of an
  // earlier turn, and a per-turn tracker would enforce nothing while looking
  // exactly like it worked.
  const churn = options.churn === false ? null : new ChurnTracker();

  // EZRA-S2 §3.13 — the BOTTOM GROUP SURGE. ⚠ OFF unless explicitly asked for
  // (D-EZ-35: the trigger face is not printed). Note the asymmetry with the line
  // above and take it literally — `churn` is built unless refused, `surge` is
  // refused unless built. One tracker for the whole race, because it counts and
  // because it carries the configured faces.
  const surge =
    options.surge === undefined || options.surge === false
      ? null
      : new SurgeTracker(
          options.surge === true ? BGS_TRIGGER_FACES_DEFAULT : (options.surge.faces ?? BGS_TRIGGER_FACES_DEFAULT),
        );

  const maxExtra = options.maxExtraTurns ?? 100;

  let state = buildStartingGrid(setup);
  const scheduledTurns = state.totalTurns;

  // D-EZ-22 — the RS trait cancels a negative Race Start Event, and it is a
  // property of the card, so it is read once rather than per turn.
  const restartExperts = new Set(
    setup.drivers.filter((c) => c.trait === 'RS').map((c) => c.driverId),
  );
  let previousWasYellowTurn = false;
  let previousWasYellowPit = false;
  // D-EZ-83 … D-EZ-87 — resolved once; absent switches are ON.
  const houseRules = houseRulesInForce(options);
  // D-EZ-85 — consecutive green-flag turns run so far (pit turns under green
  // count; a yellow turn, or a turn that brings the yellow out, resets it).
  let greenRun = 0;

  const turns: TurnResult[] = [];
  const events: (RuleEvent & { turn: number })[] = [];
  const leadChanges: LeadChange[] = [];
  const cautions: Caution[] = [];
  const careerConsequences: CareerConsequence[] = [];
  let called: RaceCalled | null = null as RaceCalled | null;

  for (;;) {
    const turn = state.turn;
    if (turn > scheduledTurns + maxExtra) {
      throw new Error(
        `race ${state.raceId} ran ${turn - 1} turns without reaching a green-flag finish — ` +
          `the "cannot end under yellow" rule (p.xv) never terminated`,
      );
    }

    const underYellow = state.yellowTurnsRemaining > 0;
    const isPitTurn = state.pitTurns.includes(turn);

    // D-EZ-22 — only this loop knows the yellow-flag history, so only it can
    // say whether a turn is a RESTART TURN. `restartEvent.ts` owns the rule;
    // this supplies the four facts it reads.
    const restartTurn =
      options.restartEvents === false
        ? false
        : isRestartTurn({
            turn,
            thisIsYellow: underYellow,
            thisIsPitTurn: isPitTurn,
            previousWasYellow: previousWasYellowTurn,
            previousWasYellowPitTurn: previousWasYellowPit,
          });

    const result = runTurn(state, track, dice, {
      ...options,
      finalTurn: turn >= scheduledTurns,
      troubleMatrix,
      crewInjured,
      lapsDown,
      churn,
      surge,
      restartTurn,
      restartExperts,
      // EZRA-S2 §4.0 RS — narrower than `restartTurn`: the Restart Duel is
      // scoped to "after a Yellow Flag", so the first turn of a race is not one.
      followsYellow: options.restartEvents === false ? false : previousWasYellowTurn || previousWasYellowPit,
      houseRules,
      // D-EZ-86 — the green-flag start, and the first green turn after a caution.
      cleanAirTurn: !underYellow && (turn === 1 || previousWasYellowTurn || previousWasYellowPit),
      greenRunBefore: greenRun,
    });

    // Carried to the NEXT iteration, which is the only place they are read.
    previousWasYellowPit = underYellow && isPitTurn;
    previousWasYellowTurn = underYellow;
    greenRun = underYellow || result.yellowCameOut ? 0 : greenRun + 1;

    retainTurnDraws(result, presentationDraws);
    presentationDraws = [];
    turns.push(result);
    // D-018 — hand the finished turn out NOW, not at the end. If the next turn
    // suspends for a die, this loop's locals never come back.
    options.onTurn?.(result);
    for (const e of result.events) events.push({ ...e, turn });
    if (result.leadChangedTo) leadChanges.push({ turn, driverId: result.leadChangedTo });
    if (result.yellowCameOut) {
      cautions.push({ turn, turns: result.yellowTurnsCalled, cars: [...new Set(result.collected)] });
    }

    state = result.state;
    for (const c of result.careerConsequences ?? []) careerConsequences.push(c);

    // D-EZ-88 — RAIN past halfway, or the event cancelled outright. "The
    // current order is the final race finish": the race stops HERE, whatever
    // the flag and however many turns were scheduled. This is the one way a
    // race ends short of its length or under yellow, and it is the sheet's.
    if (result.called) {
      called = result.called;
      break;
    }

    // p.xi / p.xv — the checkered flag only waves on a green-flag turn.
    const scheduledLengthReached = turn >= scheduledTurns;
    const ranClean = !underYellow && !result.yellowCameOut && state.yellowTurnsRemaining === 0;
    if (scheduledLengthReached && ranClean) break;
  }

  // ---- THE PHOTO FINISH (EZRA-TP) ---------------------------------------
  //
  // "When all other actions are completed on the final turn of the race." This
  // is that moment: the loop has broken on a clean green-flag finish and the
  // finishing order has not been taken yet. It runs BEFORE `finishingOrder`
  // because its whole purpose is to change one.
  //
  // Default ON; `photoFinish: false` gives the base game, where the flag falls
  // on the order the last turn left behind.
  let photoFinish: PhotoFinishResult | null = null;
  // D-EZ-88 — a called race has no last lap to race to the line on.
  if (options.photoFinish !== false && called === null) {
    dice.phase = 'PHOTO FINISH ATTEMPT (EZRA-TP)';
    const drawsBeforePfa = dice.stream.length;
    photoFinish = resolvePhotoFinish({ state, dice });
    // D-018 — the PFA happens outside any turn, so `runTurn`'s per-turn stamp
    // never sees it. Marked here over its own window for the same reason and by
    // the same rule: a hand-entered face that reaches the ledger unmarked is the
    // exact failure the requirement names.
    stampDiceEntry(
      [...photoFinish.attempts, photoFinish],
      dice.entryOver(drawsBeforePfa, dice.stream.length),
    );
    if (photoFinish.attempts.length > 0) {
      // The retired cars are untouched — a PFA is between running cars — so the
      // new order is the running list plus whoever was already out.
      state = { ...state, grid: renumber(photoFinish.order).order };
      events.push(...photoFinish.attempts.map((a) => ({ ...a, turn: turns.length })));
      events.push({ ...photoFinish, turn: turns.length });
    }
  }

  const finished: RaceState = { ...state, finished: true };
  const order = finishingOrder(finished);

  const result: RaceResult = {
    startingOrder: setup.drivers.map(driver => driver.driverId),
    // D-018 — null unless this race was hand-fed. See `RaceResult.diceStream`.
    diceStream: dice.isFed ? dice.stream : null,
    lapsDown: lapsDown ? { clamped: lapsDown.clampedCards } : null,
    churn: churn
      ? {
          triggered: churn.triggered,
          ignored: churn.ignored,
          aborted: churn.aborted,
          occurred: churn.occurred,
        }
      : null,
    surge: surge
      ? {
          triggerFaces: surge.faces,
          triggered: surge.triggered,
          aborted: surge.aborted,
          occurred: surge.occurred,
          llsgSurges: surge.llsgSurges,
          ldsgEscapes: surge.ldsgEscapes,
          chipOnly: surge.chipOnly,
          chipsAwarded: surge.chipsAwarded,
          chipsRefused: surge.chipsRefused,
          finderRolls: surge.finderRolls,
          finderRejected: surge.finderRejected,
          ungradedEscapes: surge.ungradedEscapes,
        }
      : null,
    state: finished,
    track,
    turns,
    events,
    finishingOrder: order,
    winnerId: order[0]?.driverId ?? null,
    leadChanges,
    cautions,
    turnsRun: turns.length,
    scheduledTurns,
    retirements: order.filter((f) => f.status === 'out'),
    photoFinish,
    called,
    careerConsequences,
  };
  retainFinishDraws(result, presentationDraws);
  return result;
}

/** A one-line summary for the booth, built entirely from the result. */
export function raceSummary(result: RaceResult): string {
  const winner = result.finishingOrder[0];
  return (
    `${result.track.name}: ${result.turnsRun} turns (${result.scheduledTurns} scheduled), ` +
    `${result.leadChanges.length} lead change${result.leadChanges.length === 1 ? '' : 's'}, ` +
    `${result.cautions.length} caution${result.cautions.length === 1 ? '' : 's'}, ` +
    `${result.retirements.length} car${result.retirements.length === 1 ? '' : 's'} out. ` +
    (winner ? `${winner.name} wins, leading ${winner.turnsLed} turns.` : 'Nobody finished.')
  );
}
